import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IBudgetConstruction, IBudgetFixFlip, IBudgetLand, ISpecSheet } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

export type AnyForm = IBudgetConstruction | IBudgetFixFlip | IBudgetLand | ISpecSheet;

/**
 * Reads and writes a single field on a form identified by (formType, propertyGuid)
 * in the React Query application cache.
 *
 * Parallel to `usePropertyField`, but scoped to `information.properties.forms[]`
 * instead of `information.properties.items[]`.
 *
 * @param propertyGuid - GUID of the property the form belongs to
 * @param formType     - form type discriminant (e.g. 'Budget', 'Fix-Flip', 'Land Budget', 'Spec Sheet')
 * @param selector     - reads the field value from the matched form
 * @param updater      - returns a new form with the field updated
 * @returns            [currentValue, setter]
 *
 * @example
 * const [formName, setFormName] = useFormField(
 *     propertyGuid,
 *     'Budget',
 *     (f) => f.name,
 *     (f, v) => ({ ...f, name: v })
 * );
 */
export function useFormField<T>(
    propertyGuid: string,
    formType: string,
    selector: (form: AnyForm) => T,
    updater: (form: AnyForm, value: T) => AnyForm
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const form = d.information?.properties?.forms?.find(
                (f) => f.type === formType && f.propertyGuid === propertyGuid
            );
            return form !== undefined ? selector(form) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const forms = old.information?.properties?.forms ?? [];
            const idx = forms.findIndex((f) => f.type === formType && f.propertyGuid === propertyGuid);
            if (idx === -1) return old;
            const newForms = [...forms];
            newForms[idx] = updater(newForms[idx], value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: newForms,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
